import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Maximize2, Info, ChevronDown } from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  ScreenerService,
  type MetricsSeriesResponse,
  type MetricSeries,
  type ValuationSeriesResponse,
} from "../../services/screener-service";

interface FinancialChartsTabProps {
  assetId: string;
  currency?: string;
}

// Chart definition with configuration
interface ChartConfig {
  key: string;
  title: string;
  description: string; // Tooltip explanation of the metric
  metrics: string[];
  type: "bar" | "line";
  colors: string[];
  unit: "currency" | "percentage" | "shares" | "ratio";
  showZeroLine?: boolean;
  isValuation?: boolean; // If true, fetch from valuation-series endpoint
  category:
    | "income"
    | "cashflow"
    | "margins"
    | "balance"
    | "returns"
    | "valuation";
}

// Chart categories for grouping
const CHART_CATEGORIES = [
  { key: "income", title: "Income & Revenue" },
  { key: "cashflow", title: "Cash Flow" },
  { key: "margins", title: "Margins" },
  { key: "balance", title: "Balance Sheet" },
  { key: "returns", title: "Returns & Ratios" },
  { key: "valuation", title: "Valuation" },
] as const;

// All charts to display with descriptions
const CHARTS: ChartConfig[] = [
  // Income & Revenue
  {
    key: "revenue",
    title: "Revenue",
    description:
      "Total revenue/sales. Growing revenue indicates business expansion.",
    metrics: ["revenue"],
    type: "bar",
    colors: ["#3b82f6"],
    unit: "currency",
    category: "income",
  },
  {
    key: "net_income",
    title: "Net Income",
    description: "Profit after all expenses. Negative values indicate losses.",
    metrics: ["net_income"],
    type: "bar",
    colors: ["#10b981"],
    unit: "currency",
    showZeroLine: true,
    category: "income",
  },
  {
    key: "operating_income",
    title: "Operating Income",
    description: "Profit from core operations before interest and taxes.",
    metrics: ["operating_income"],
    type: "bar",
    colors: ["#8b5cf6"],
    unit: "currency",
    showZeroLine: true,
    category: "income",
  },
  {
    key: "ebitda",
    title: "EBITDA",
    description:
      "Earnings before interest, taxes, depreciation & amortization. Shows operating profitability.",
    metrics: ["ebitda"],
    type: "bar",
    colors: ["#f59e0b"],
    unit: "currency",
    category: "income",
  },
  {
    key: "eps",
    title: "EPS",
    description: "Earnings Per Share. Higher is better. Compare YoY growth.",
    metrics: ["eps"],
    type: "bar",
    colors: ["#22c55e"],
    unit: "currency",
    category: "income",
  },

  // Cash Flow
  {
    key: "fcf_sbc",
    title: "FCF & SBC",
    description:
      "Free Cash Flow vs Stock-Based Compensation. High SBC relative to FCF dilutes shareholders.",
    metrics: ["free_cash_flow", "stock_based_compensation"],
    type: "bar",
    colors: ["#10b981", "#ef4444"],
    unit: "currency",
    showZeroLine: true,
    category: "cashflow",
  },
  {
    key: "capex",
    title: "CapEx",
    description:
      "Capital Expenditure. Investment in property, plant & equipment.",
    metrics: ["capex"],
    type: "bar",
    colors: ["#06b6d4"],
    unit: "currency",
    category: "cashflow",
  },
  {
    key: "dividends",
    title: "Dividends Paid",
    description:
      "Cash returned to shareholders. Growing dividends signal financial health.",
    metrics: ["dividends_paid"],
    type: "bar",
    colors: ["#ec4899"],
    unit: "currency",
    category: "cashflow",
  },

  // Margins (each in its own chart)
  {
    key: "gross_margin",
    title: "Gross Margin",
    description:
      "Gross profit as percentage of revenue. Higher margins indicate pricing power and efficiency.",
    metrics: ["gross_margin"],
    type: "line",
    colors: ["#3b82f6"],
    unit: "percentage",
    category: "margins",
  },
  {
    key: "operating_margin",
    title: "Operating Margin",
    description:
      "Operating income as percentage of revenue. Shows profitability from core operations.",
    metrics: ["operating_margin"],
    type: "line",
    colors: ["#8b5cf6"],
    unit: "percentage",
    category: "margins",
  },
  {
    key: "net_margin",
    title: "Net Margin",
    description:
      "Net income as percentage of revenue. The bottom-line profitability metric.",
    metrics: ["net_margin"],
    type: "line",
    colors: ["#10b981"],
    unit: "percentage",
    category: "margins",
  },
  {
    key: "fcf_margin",
    title: "FCF Margin",
    description:
      "Free cash flow as percentage of revenue. Shows cash generation efficiency.",
    metrics: ["fcf_margin"],
    type: "line",
    colors: ["#06b6d4"],
    unit: "percentage",
    category: "margins",
  },

  // Balance Sheet
  {
    key: "debt_cash",
    title: "Debt & Cash",
    description:
      "Total debt vs cash position. More cash than debt is a healthy sign.",
    metrics: ["total_debt", "total_cash"],
    type: "bar",
    colors: ["#ef4444", "#10b981"],
    unit: "currency",
    category: "balance",
  },
  {
    key: "net_debt",
    title: "Net Debt",
    description:
      "Total Debt minus Cash. Negative net debt means more cash than debt.",
    metrics: ["net_debt"],
    type: "bar",
    colors: ["#f97316"],
    unit: "currency",
    showZeroLine: true,
    category: "balance",
  },
  {
    key: "equity",
    title: "Stockholders Equity",
    description:
      "Book value of shareholder ownership. Growing equity indicates value creation.",
    metrics: ["stockholders_equity"],
    type: "bar",
    colors: ["#0ea5e9"],
    unit: "currency",
    category: "balance",
  },
  {
    key: "shares",
    title: "Shares Outstanding",
    description: "Number of shares. Declining shares (buybacks) can boost EPS.",
    metrics: ["shares_outstanding"],
    type: "bar",
    colors: ["#64748b"],
    unit: "shares",
    category: "balance",
  },

  // Returns & Ratios
  {
    key: "roe",
    title: "ROE",
    description:
      "Return on Equity. >15% is generally considered good. Measures profitability vs shareholder investment.",
    metrics: ["roe"],
    type: "line",
    colors: ["#f59e0b"],
    unit: "percentage",
    category: "returns",
  },
  {
    key: "debt_to_equity",
    title: "Debt/Equity",
    description:
      "Leverage ratio. Lower is typically safer. High D/E increases risk but may boost returns.",
    metrics: ["debt_to_equity"],
    type: "line",
    colors: ["#ef4444"],
    unit: "ratio",
    category: "returns",
  },

  // Valuation (from valuation-series endpoint)
  {
    key: "price_to_sales",
    title: "P/S Ratio",
    description:
      "Price-to-Sales. Useful for unprofitable companies. Lower may indicate undervaluation.",
    metrics: ["price_to_sales"],
    type: "line",
    colors: ["#3b82f6"],
    unit: "ratio",
    isValuation: true,
    category: "valuation",
  },
  {
    key: "price_to_earnings",
    title: "P/E Ratio",
    description:
      "Price-to-Earnings. Most common valuation metric. Compare to industry peers and historical average.",
    metrics: ["price_to_earnings"],
    type: "line",
    colors: ["#8b5cf6"],
    unit: "ratio",
    isValuation: true,
    category: "valuation",
  },
  {
    key: "price_to_fcf",
    title: "P/FCF Ratio",
    description:
      "Price-to-Free-Cash-Flow. Often more reliable than P/E as cash is harder to manipulate.",
    metrics: ["price_to_fcf"],
    type: "line",
    colors: ["#10b981"],
    unit: "ratio",
    isValuation: true,
    category: "valuation",
  },
];

// Chart data point with periodKey and metric values
interface ChartDataPoint {
  periodKey: string;
  [metric: string]: string | number | undefined;
}

// Get all unique metrics needed
const ALL_METRICS = [...new Set(CHARTS.flatMap((c) => c.metrics))];

// Format number for display
const formatValue = (value: number, unit: string): string => {
  if (unit === "percentage") return `${value.toFixed(1)}%`;
  if (unit === "ratio") return value.toFixed(2);
  if (unit === "shares") {
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(0)}M`;
    return value.toFixed(0);
  }
  // Currency
  const absValue = Math.abs(value);
  if (absValue >= 1e12) return `${(value / 1e12).toFixed(1)}T`;
  if (absValue >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `${(value / 1e6).toFixed(0)}M`;
  if (absValue >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
  return value.toFixed(0);
};

type PeriodType = "annual" | "quarterly" | "daily";
type ValuationBasis = "ttm" | "annual";

// Get period key from date (YYYY for annual, YYYY-Qn for quarterly, YYYY-MM-DD for daily)
const getPeriodKey = (dateStr: string, periodType: PeriodType): string => {
  const date = new Date(dateStr);
  const year = date.getFullYear();

  if (periodType === "quarterly") {
    // Calculate quarter from month: Q1 (Jan-Mar), Q2 (Apr-Jun), Q3 (Jul-Sep), Q4 (Oct-Dec)
    const month = date.getMonth(); // 0-11
    const quarter = Math.floor(month / 3) + 1;
    return `${year}-Q${quarter}`;
  }

  if (periodType === "daily") {
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return year.toString();
};

// Get display label for period key
const getPeriodLabel = (
  periodKey: string | number | undefined,
  periodType: PeriodType,
): string => {
  // Handle non-string values that might come from recharts tooltip
  if (periodKey === undefined || periodKey === null) {
    return "";
  }

  const key = String(periodKey);

  if (periodType === "quarterly") {
    // Only split if it looks like a quarterly key (contains -Q)
    if (key.includes("-Q")) {
      const [year, quarter] = key.split("-Q");
      return `Q${quarter} ${year}`;
    }
    return key; // Return as-is if not in expected format
  }
  if (periodType === "daily") {
    const date = new Date(key);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    return key;
  }
  return `FY ${key}`;
};

// Calculate YoY percentage change
const calculateYoY = (
  data: ChartDataPoint[],
  metric: string,
  periodType: PeriodType,
): ChartDataPoint[] => {
  if (data.length < 2) return data;

  return data.map((point) => {
    const currentValue = point[metric];
    if (typeof currentValue !== "number") {
      return { ...point, [`${metric}_yoy`]: undefined };
    }

    let previousValue: number | undefined;

    if (periodType === "daily") {
      return { ...point, [`${metric}_yoy`]: undefined };
    }

    if (periodType === "annual") {
      // Compare with previous year (index - 1 since data is sorted chronologically)
      const currentIndex = data.indexOf(point);
      if (currentIndex > 0) {
        const prevVal = data[currentIndex - 1][metric];
        if (typeof prevVal === "number") previousValue = prevVal;
      }
    } else {
      // Quarterly: compare with same quarter from previous year
      const currentPeriodKey = point.periodKey;
      if (currentPeriodKey.includes("-Q")) {
        const [year, quarter] = currentPeriodKey.split("-Q");
        const prevYearKey = `${parseInt(year) - 1}-Q${quarter}`;
        const prevYearPoint = data.find((p) => p.periodKey === prevYearKey);
        if (prevYearPoint) {
          const prevVal = prevYearPoint[metric];
          if (typeof prevVal === "number") previousValue = prevVal;
        }
      }
    }

    if (previousValue === undefined || previousValue === 0) {
      return { ...point, [`${metric}_yoy`]: undefined };
    }

    const yoy = (currentValue / previousValue - 1) * 100;
    return { ...point, [`${metric}_yoy`]: yoy };
  });
};

// Calculate CAGR (Compound Annual Growth Rate)
const calculateCAGR = (
  data: ChartDataPoint[],
  metric: string,
): number | null => {
  if (data.length < 2) return null;

  const firstPoint = data[0];
  const lastPoint = data[data.length - 1];

  const startValue = firstPoint[metric];
  const endValue = lastPoint[metric];

  if (
    typeof startValue !== "number" ||
    typeof endValue !== "number" ||
    startValue <= 0 ||
    endValue <= 0
  ) {
    return null;
  }

  // Calculate number of years between first and last period
  let years: number;

  if (firstPoint.periodKey.includes("-Q")) {
    // Quarterly: calculate exact years between quarters
    const [startYear, startQ] = firstPoint.periodKey.split("-Q");
    const [endYear, endQ] = lastPoint.periodKey.split("-Q");
    const startYearNum = parseInt(startYear);
    const endYearNum = parseInt(endYear);
    const startQNum = parseInt(startQ);
    const endQNum = parseInt(endQ);

    // Calculate quarters difference
    const quartersDiff =
      (endYearNum - startYearNum) * 4 + (endQNum - startQNum);
    years = quartersDiff / 4;
  } else if (firstPoint.periodKey.includes("-")) {
    const startDate = new Date(firstPoint.periodKey);
    const endDate = new Date(lastPoint.periodKey);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;
    years =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
  } else {
    // Annual: years difference
    const startYear = parseInt(firstPoint.periodKey);
    const endYear = parseInt(lastPoint.periodKey);
    years = endYear - startYear;
  }

  if (years <= 0) return null;

  const cagr = (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
  return cagr;
};

// Merge multiple metrics into chart data
const buildChartData = (
  series: Record<string, MetricSeries>,
  metrics: string[],
  periodType: PeriodType,
): ChartDataPoint[] => {
  const periodsMap = new Map<string, ChartDataPoint & { _date?: number }>();

  for (const metric of metrics) {
    const metricSeries = series[metric];
    if (!metricSeries) continue;

    for (const dp of metricSeries.data_points) {
      const parsedDate = new Date(dp.period_end_date);
      const timestamp = parsedDate.getTime();
      if (!Number.isFinite(timestamp)) {
        continue;
      }
      const periodKey = getPeriodKey(dp.period_end_date, periodType);
      if (!periodKey) continue;
      const existing = periodsMap.get(periodKey) || { periodKey };
      existing[metric] = dp.value;
      // Store original date for sorting
      if (existing._date === undefined) {
        existing._date = timestamp;
      }
      periodsMap.set(periodKey, existing);
    }
  }

  // Sort by date (ascending)
  return Array.from(periodsMap.values())
    .sort((a, b) => (a._date ?? 0) - (b._date ?? 0))
    .map((entry) => {
      const rest = { ...entry } as ChartDataPoint & { _date?: number };
      delete rest._date;
      return rest;
    });
};

const isValuationSeriesEmpty = (
  response: ValuationSeriesResponse | null,
): boolean => {
  if (!response?.series) return true;
  const hasSeriesData = Object.values(response.series).some(
    (series) => series.data_points?.length,
  );
  const hasLatest = response.latest
    ? Object.values(response.latest).some((value) => typeof value === "number")
    : false;
  return !hasSeriesData && !hasLatest;
};

// Mini chart component
function MiniChart({
  config,
  data,
  expanded = false,
  periodType,
  mode = "value",
}: {
  config: ChartConfig;
  data: ChartDataPoint[];
  expanded?: boolean;
  periodType: PeriodType;
  mode?: "value" | "yoy";
}) {
  const height = expanded ? 300 : 120;
  const showAxis = expanded;

  // Keep daily points as-is for valuation charts
  const displayData = data;

  if (displayData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground text-xs">
        No data
      </div>
    );
  }

  const ChartComponent = config.type === "line" ? LineChart : BarChart;

  // Use smaller dots for valuation charts
  const dotSize = config.isValuation ? 1 : 3;
  const formatTooltipValue = (value: number) => {
    if (mode === "yoy") {
      return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
    }
    return formatValue(value, config.unit);
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ChartComponent
        data={displayData}
        margin={{ top: 5, right: 5, left: expanded ? 40 : 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
        {showAxis && (
          <>
            <XAxis
              dataKey="periodKey"
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => getPeriodLabel(value, periodType)}
            />
            <YAxis
              tickFormatter={(v) => {
                if (mode === "yoy") {
                  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
                }
                return formatValue(v, config.unit);
              }}
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
          </>
        )}
        <RechartsTooltip
          content={({ active, payload, label }) => {
            if (!active || !payload || payload.length === 0) return null;
            const rawLabel =
              payload[0]?.payload?.periodKey !== undefined
                ? payload[0].payload.periodKey
                : label;
            const labelText =
              rawLabel !== undefined && rawLabel !== null
                ? getPeriodLabel(String(rawLabel), periodType)
                : "";

            return (
              <div className="bg-card border border-border rounded-md px-2 py-1.5 text-[11px] shadow-sm">
                {labelText && (
                  <div className="text-muted-foreground mb-1">{labelText}</div>
                )}
                <div className="space-y-0.5">
                  {payload.map((entry) => {
                    const name = String(entry.name || "").replace(/_/g, " ");
                    const value = entry.value;
                    const formatted =
                      typeof value === "number" && Number.isFinite(value)
                        ? formatTooltipValue(value)
                        : "-";
                    return (
                      <div
                        key={`${name}`}
                        className="flex items-center justify-between gap-4"
                      >
                        <span className="text-foreground">{name}</span>
                        <span className="font-mono">{formatted}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }}
        />
        {config.showZeroLine && (
          <ReferenceLine y={0} stroke="hsl(var(--border))" />
        )}
        {config.type === "bar"
          ? config.metrics.map((metric, idx) => (
              <Bar
                key={metric}
                dataKey={mode === "yoy" ? `${metric}_yoy` : metric}
                fill={config.colors[idx] || config.colors[0]}
                radius={[2, 2, 0, 0]}
                name={metric.replace(/_/g, " ")}
              />
            ))
          : config.metrics.map((metric, idx) => (
              <Line
                key={metric}
                type="monotone"
                dataKey={mode === "yoy" ? `${metric}_yoy` : metric}
                stroke={config.colors[idx] || config.colors[0]}
                strokeWidth={2}
                dot={{ r: dotSize }}
                name={metric.replace(/_/g, " ")}
              />
            ))}
      </ChartComponent>
    </ResponsiveContainer>
  );
}

// Expandable chart card
function ChartCard({
  config,
  series,
  periodType,
  latestValue,
  valuationBasis,
}: {
  config: ChartConfig;
  series: Record<string, MetricSeries>;
  periodType: PeriodType;
  latestValue?: number;
  valuationBasis?: ValuationBasis;
}) {
  const [mode, setMode] = useState<"value" | "yoy">("value");
  const chartPeriodType: PeriodType = config.isValuation ? "daily" : periodType;

  // Only allow YoY toggle for currency and shares (not percentages/ratios)
  const canToggleYoY = config.unit === "currency" || config.unit === "shares";

  const rawData = useMemo(
    () => buildChartData(series, config.metrics, chartPeriodType),
    [series, config.metrics, chartPeriodType],
  );

  // Calculate YoY data if needed
  const dataWithYoY = useMemo(() => {
    if (mode === "yoy" && canToggleYoY) {
      let result = rawData;
      for (const metric of config.metrics) {
        result = calculateYoY(result, metric, chartPeriodType);
      }
      return result;
    }
    return rawData;
  }, [rawData, mode, config.metrics, chartPeriodType, canToggleYoY]);

  const chartData = mode === "yoy" && canToggleYoY ? dataWithYoY : rawData;

  // Calculate CAGR from raw value data (main metric)
  const cagr = useMemo(() => {
    if (rawData.length < 2 || config.metrics.length === 0) return null;
    return calculateCAGR(rawData, config.metrics[0]);
  }, [rawData, config.metrics]);

  const hasData =
    chartData.length > 0 &&
    config.metrics.some((m) => {
      const key = mode === "yoy" && canToggleYoY ? `${m}_yoy` : m;
      return chartData.some((d) => d[key] !== undefined);
    });

  if (!hasData) return null;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="py-2 px-3 flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <CardTitle className="text-xs font-medium truncate">
            {config.title}
          </CardTitle>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground/60 cursor-help flex-shrink-0" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[280px] text-xs">
                {config.description}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {latestValue !== undefined && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
              {formatValue(latestValue, config.unit)}
            </Badge>
          )}
          {chartPeriodType === "daily" && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
            Daily ({(valuationBasis ?? "ttm").toUpperCase()})
            </Badge>
          )}
          {cagr !== null && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
              CAGR: {cagr >= 0 ? "+" : ""}
              {cagr.toFixed(1)}%/yr
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          {canToggleYoY && (
            <div className="flex items-center gap-0.5 border rounded-md p-0.5">
              <Button
                variant={mode === "value" ? "default" : "ghost"}
                size="sm"
                className="h-5 px-2 text-[10px]"
                onClick={() => setMode("value")}
              >
                Value
              </Button>
              <Button
                variant={mode === "yoy" ? "default" : "ghost"}
                size="sm"
                className="h-5 px-2 text-[10px]"
                onClick={() => setMode("yoy")}
              >
                %YoY
              </Button>
            </div>
          )}
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" className="h-5 w-5">
                <Maximize2 className="h-3 w-3" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <DialogTitle>{config.title}</DialogTitle>
                  {latestValue !== undefined && (
                    <Badge variant="outline" className="text-xs px-2 py-0.5">
                      {formatValue(latestValue, config.unit)}
                    </Badge>
                  )}
                  {cagr !== null && (
                    <Badge variant="secondary" className="text-xs px-2 py-0.5">
                      CAGR: {cagr >= 0 ? "+" : ""}
                      {cagr.toFixed(1)}%/yr
                    </Badge>
                  )}
                </div>
              </DialogHeader>
              {canToggleYoY && (
                <div className="flex items-center gap-2 pb-2">
                  <div className="flex items-center gap-0.5 border rounded-md p-0.5">
                    <Button
                      variant={mode === "value" ? "default" : "ghost"}
                      size="sm"
                      className="h-6 px-3 text-xs"
                      onClick={() => setMode("value")}
                    >
                      Value
                    </Button>
                    <Button
                      variant={mode === "yoy" ? "default" : "ghost"}
                      size="sm"
                      className="h-6 px-3 text-xs"
                      onClick={() => setMode("yoy")}
                    >
                      %YoY
                    </Button>
                  </div>
                </div>
              )}
              <div className="h-80">
                <MiniChart
                  config={config}
                  data={chartData}
                  expanded
                  periodType={chartPeriodType}
                  mode={mode}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-2 pt-0">
        <MiniChart
          config={config}
          data={chartData}
          periodType={chartPeriodType}
          mode={mode}
        />
      </CardContent>
    </Card>
  );
}

export function FinancialChartsTab({ assetId }: FinancialChartsTabProps) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<MetricsSeriesResponse | null>(null);
  const [valuationData, setValuationData] =
    useState<ValuationSeriesResponse | null>(null);
  const [valuationBasis, setValuationBasis] = useState<ValuationBasis>("ttm");
  const [period, setPeriod] = useState<"annual" | "quarterly">("annual");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all metrics including computed ratios
        const metricsToFetch = [
          ...ALL_METRICS,
          // Dependencies for computed ratios
          "gross_profit",
          "revenue",
          "operating_income",
          "net_income",
          "stockholders_equity",
          "total_debt",
        ];
        const metricsPromise = ScreenerService.getMetricsSeries(assetId, period, [
          ...new Set(metricsToFetch),
        ]);

        let valuationResult: ValuationSeriesResponse | null = null;
        let basisUsed: ValuationBasis = "ttm";

        try {
          const ttmResponse = await ScreenerService.getValuationSeries(assetId, {
            periodType: period,
            granularity: "daily",
            basis: "ttm",
          });
          if (!isValuationSeriesEmpty(ttmResponse)) {
            valuationResult = ttmResponse;
            basisUsed = "ttm";
          } else {
            const annualResponse = await ScreenerService.getValuationSeries(
              assetId,
              {
                periodType: period,
                granularity: "daily",
                basis: "annual",
              },
            );
            valuationResult = annualResponse;
            basisUsed = "annual";
          }
        } catch (err) {
          console.error("Failed to fetch valuation series TTM:", err);
          try {
            const annualResponse = await ScreenerService.getValuationSeries(
              assetId,
              {
                periodType: period,
                granularity: "daily",
                basis: "annual",
              },
            );
            valuationResult = annualResponse;
            basisUsed = "annual";
          } catch (annualErr) {
            console.error("Failed to fetch valuation series annual:", annualErr);
          }
        }

        try {
          const metricsResponse = await metricsPromise;
          setData(metricsResponse);
        } catch (metricsErr) {
          console.error("Failed to fetch metrics series:", metricsErr);
        }

        if (valuationResult) {
          setValuationData(valuationResult);
          setValuationBasis(basisUsed);
        } else {
          setValuationData(null);
          setValuationBasis("ttm");
        }
      } catch (err) {
        console.error("Failed to fetch financial data:", err);
        setError("Failed to load financial data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [assetId, period]);

  // Merge valuation series into main data if available
  // MUST be called before early returns to maintain consistent hook order
  const mergedSeries = useMemo(() => {
    if (!data) return {};
    const result = { ...data.series };
    if (valuationData?.series) {
      Object.assign(result, valuationData.series);
    }
    return result;
  }, [data, valuationData]);

  // Get latest values for valuation charts - memoized to avoid recreating function
  // MUST be called before early returns to maintain consistent hook order
  const getLatestValue = useMemo(() => {
    return (config: ChartConfig): number | undefined => {
      if (!config.isValuation || !valuationData?.latest) return undefined;
      switch (config.key) {
        case "price_to_sales":
          return valuationData.latest.price_to_sales;
        case "price_to_earnings":
          return valuationData.latest.price_to_earnings;
        case "price_to_fcf":
          return valuationData.latest.price_to_fcf;
        default:
          return undefined;
      }
    };
  }, [valuationData]);

  // Filter charts that have data - MUST be memoized to avoid changing number of hooks
  // MUST be called before early returns to maintain consistent hook order
  const chartsWithData = useMemo(() => {
    if (!data) return [];
    return CHARTS.filter((config) => {
      const seriesToUse =
        config.isValuation && valuationData
          ? valuationData.series
          : mergedSeries;
      const chartPeriodType: PeriodType = config.isValuation ? "daily" : period;
      const chartData = buildChartData(
        seriesToUse,
        config.metrics,
        chartPeriodType,
      );
      return (
        chartData.length > 0 &&
        config.metrics.some((m) => chartData.some((d) => d[m] !== undefined))
      );
    });
  }, [mergedSeries, valuationData, period, data]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="h-40" />
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <p>{error || "No data available"}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Period Toggle */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">
          {chartsWithData.length} charts available
        </span>
        <Tabs
          value={period}
          onValueChange={(v) => setPeriod(v as "annual" | "quarterly")}
        >
          <TabsList variant="line">
            <TabsTrigger value="annual" className="min-h-8 text-xs px-3 py-1.5">
              Annual
            </TabsTrigger>
            <TabsTrigger value="quarterly" className="min-h-8 text-xs px-3 py-1.5">
              Quarterly
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Charts grouped by category */}
      <div className="space-y-4">
        {CHART_CATEGORIES.map((category) => {
          const categoryCharts = chartsWithData.filter(
            (c) => c.category === category.key,
          );
          if (categoryCharts.length === 0) return null;

          return (
            <Collapsible key={category.key} defaultOpen className="space-y-2">
              <CollapsibleTrigger className="flex items-center gap-2 w-full text-left hover:bg-muted/50 rounded-md px-2 py-1.5 transition-colors group">
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
                <span className="text-sm font-medium">{category.title}</span>
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 h-4 ml-auto"
                >
                  {categoryCharts.length}
                </Badge>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pl-6">
                  {categoryCharts.map((config) => {
                    const seriesToUse =
                      config.isValuation && valuationData
                        ? valuationData.series
                        : mergedSeries;
                    return (
                      <ChartCard
                        key={config.key}
                        config={config}
                        series={seriesToUse}
                        periodType={period}
                        latestValue={getLatestValue(config)}
                        valuationBasis={
                          config.isValuation ? valuationBasis : undefined
                        }
                      />
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>

      {chartsWithData.length === 0 && (
        <div className="flex items-center justify-center h-40 text-muted-foreground">
          <p>No financial data available for this asset</p>
        </div>
      )}
    </div>
  );
}
